// Producer ADR Script Book.
//
// Toggleable views (by-scene / by-character), inline cue editor, bulk
// operations (split / merge / delete / reassign), and exports. Selection +
// edit state are owned here so the keyboard handlers / bulk-actions can
// see them.

import { copy } from '@ohear/shared'
import type { FrameRate } from '@ohear/shared'
import {
  Button,
  Card,
  EmptyState,
  Modal,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { ExportButtons, type ExportItem } from '../../../features/exports'
import {
  ScriptBookByCharacter,
  ScriptBookByScene,
  type ScriptBookByScenePropsRow,
  buildCharacterColorMap,
} from '../../../features/script-book'
import { useCharacters } from '../../../lib/api/characters'
import { useEpisodes } from '../../../lib/api/episodes'
import { useProducerProject, useUpdateProjectStatus } from '../../../lib/api/producer-projects'
import type { CueRow } from '../../../lib/api/producer-types'
import {
  useBulkDeleteCues,
  useBulkReassignCharacter,
  useEpisodeCues,
  useMergeCues,
  useScenes,
  useSplitCue,
  useUpdateCue,
} from '../../../lib/api/scenes'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

export const Route = createFileRoute('/producer/projects/$projectId/script-book')({
  component: ScriptBookPage,
})

function ScriptBookPage() {
  useRequireRole(['owner', 'member'])
  const t = useT()
  const { projectId } = Route.useParams()
  const projectQ = useProducerProject(projectId)
  const episodesQ = useEpisodes(projectId)

  const [episodeId, setEpisodeId] = useState<string | null>(null)
  const effectiveEpisodeId = episodeId ?? episodesQ.data?.[0]?.id ?? null

  const scenesQ = useScenes(effectiveEpisodeId ?? undefined)
  const cuesQ = useEpisodeCues(effectiveEpisodeId ?? undefined)
  const charactersQ = useCharacters(projectId)

  const updateCue = useUpdateCue(effectiveEpisodeId ?? '')
  const splitCue = useSplitCue(effectiveEpisodeId ?? '')
  const mergeCues = useMergeCues(effectiveEpisodeId ?? '')
  const bulkDelete = useBulkDeleteCues(effectiveEpisodeId ?? '')
  const bulkReassign = useBulkReassignCharacter(effectiveEpisodeId ?? '')
  const updateStatus = useUpdateProjectStatus(projectId)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editingCueId, setEditingCueId] = useState<string | null>(null)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [reassignTo, setReassignTo] = useState<string>('')

  const characters = charactersQ.data ?? []
  const cues = cuesQ.data ?? []
  const scenes = scenesQ.data ?? []
  const characterColors = useMemo(() => buildCharacterColorMap(characters), [characters])

  const episode = episodesQ.data?.find((e) => e.id === effectiveEpisodeId) ?? null
  const frameRate: FrameRate = episode
    ? { fps: episode.frame_rate_fps, dropFrame: episode.drop_frame }
    : { fps: 24, dropFrame: false }

  const rowState = useMemo(() => {
    const m = new Map<string, ScriptBookByScenePropsRow>()
    for (const c of cues) {
      m.set(c.id, {
        selected: selected.has(c.id),
        isEditing: editingCueId === c.id,
      })
    }
    return m
  }, [cues, selected, editingCueId])

  const onToggleSelect = (cueId: string, next: boolean) => {
    setSelected((prev) => {
      const copy = new Set(prev)
      if (next) copy.add(cueId)
      else copy.delete(cueId)
      return copy
    })
  }

  const onSplit = (cueId: string) => {
    const cue = cues.find((c) => c.id === cueId)
    if (!cue) return
    const mid = (cue.in_sec + cue.out_sec) / 2
    void splitCue.mutateAsync({ cue_id: cueId, split_at_sec: mid })
  }
  const onMerge = (cueId: string) => {
    if (selected.size < 2) return
    void mergeCues.mutateAsync({ cue_ids: [...selected, cueId] })
  }
  const onSave = (cueId: string, patch: Partial<CueRow>) => {
    void updateCue.mutateAsync({ cue_id: cueId, ...stripUndefined(patch) })
    setEditingCueId(null)
  }

  const bulkOps = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[12px] text-[var(--text-2)]">{selected.size} selected</span>
      <Button
        size="sm"
        variant="ghost"
        disabled={selected.size === 0}
        onClick={() => {
          if (selected.size === 0) return
          void bulkDelete.mutateAsync({ cue_ids: [...selected] })
          setSelected(new Set())
        }}
      >
        Delete
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={selected.size < 2}
        onClick={() => void mergeCues.mutateAsync({ cue_ids: [...selected] })}
      >
        Merge
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={selected.size === 0}
        onClick={() => setReassignOpen(true)}
      >
        Reassign character
      </Button>
    </div>
  )

  const canApprove =
    projectQ.data?.status === 'preview' &&
    cues.length > 0 &&
    characters.every((c) => c.line_count >= 0)

  if (projectQ.isLoading || episodesQ.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
          <Spinner /> Loading script book…
        </div>
      </Card>
    )
  }
  if (!projectQ.data) {
    return (
      <Card>
        <p className="m-0 text-[13px] text-[var(--red)]">{t(copy.common.NOT_FOUND.project)}</p>
      </Card>
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[var(--border)]">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Script book
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
            {projectQ.data.name}
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            <span className="tabular-nums font-medium text-[var(--text)]">{cues.length}</span> cue
            {cues.length === 1 ? '' : 's'} across{' '}
            <span className="tabular-nums">{scenes.length}</span> scene
            {scenes.length === 1 ? '' : 's'}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Episode"
            value={effectiveEpisodeId ?? ''}
            onChange={(e) => setEpisodeId(e.target.value)}
          >
            {(episodesQ.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                Episode {e.episode_number} · {e.title}
              </option>
            ))}
          </Select>
          <ExportButtons exports={scriptBookExportItems(projectId)} />
          <Button
            variant="primary"
            disabled={!canApprove || updateStatus.isPending}
            loading={updateStatus.isPending}
            onClick={() => void updateStatus.mutateAsync({ status: 'recording' })}
          >
            Approve & Open Recording
          </Button>
        </div>
      </header>

      <Card actions={bulkOps} data-tour="cue-list">
        {cuesQ.isLoading ? (
          <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
            <Spinner /> Loading cues…
          </div>
        ) : cues.length === 0 ? (
          <EmptyState
            title="No cues yet"
            description="Run preprocessing to populate the script book."
          />
        ) : (
          <Tabs defaultValue="by-scene">
            <TabList aria-label="Script book view">
              <Tab value="by-scene">By scene</Tab>
              <Tab value="by-character">By character</Tab>
            </TabList>
            <TabPanel value="by-scene">
              <ScriptBookByScene
                scenes={scenes}
                cues={cues}
                frameRate={frameRate}
                rowState={rowState}
                characterColors={characterColors}
                onToggleSelect={onToggleSelect}
                onStartEdit={(id) => setEditingCueId(id)}
                onCancelEdit={() => setEditingCueId(null)}
                onSave={onSave}
                onSplit={onSplit}
                onMerge={onMerge}
              />
            </TabPanel>
            <TabPanel value="by-character">
              <ScriptBookByCharacter
                characters={characters}
                cues={cues}
                frameRate={frameRate}
                rowState={rowState}
                characterColors={characterColors}
                onToggleSelect={onToggleSelect}
                onStartEdit={(id) => setEditingCueId(id)}
                onCancelEdit={() => setEditingCueId(null)}
                onSave={onSave}
                onSplit={onSplit}
                onMerge={onMerge}
              />
            </TabPanel>
          </Tabs>
        )}
      </Card>

      <Modal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        title="Reassign cues to character"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReassignOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!reassignTo}
              onClick={() => {
                if (!reassignTo) return
                void bulkReassign.mutateAsync({
                  cue_ids: [...selected],
                  character_id: reassignTo,
                })
                setReassignOpen(false)
                setSelected(new Set())
              }}
            >
              Reassign {selected.size}
            </Button>
          </>
        }
      >
        <Select
          aria-label="Target character"
          value={reassignTo}
          onChange={(e) => setReassignTo(e.target.value)}
        >
          <option value="">{t(copy.common.PLACEHOLDERS.pick_character)}</option>
          {characters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name} ({c.character_code})
            </option>
          ))}
        </Select>
      </Modal>
    </section>
  )
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out as T
}

function scriptBookExportItems(projectId: string): ExportItem[] {
  return [
    {
      label: 'PDF (by scene)',
      format: 'pdf',
      endpoint: '/api/v1/exports/script-book',
      params: { project_id: projectId, view: 'by_scene' },
    },
    {
      label: 'PDF (by character)',
      format: 'pdf',
      endpoint: '/api/v1/exports/script-book',
      params: { project_id: projectId, view: 'by_character' },
    },
    {
      label: 'CSV cue sheet',
      format: 'csv',
      endpoint: '/api/v1/exports/cue-sheet',
      params: { project_id: projectId, format: 'csv' },
    },
    {
      label: 'JSON cue sheet',
      format: 'json',
      endpoint: '/api/v1/exports/cue-sheet',
      params: { project_id: projectId, format: 'json' },
    },
  ]
}
